/**
 * Kontaminasyon Erken Uyarı Sistemi
 *
 * TVOC + CO₂ + inkubasyon hız üçlüsüne dayalı "Kontaminasyon Risk Skoru" (0-100)
 * Risk > 60 → Telegram: "LM-008 şüpheli — bugün muayene et"
 * Risk > 80 → Kırmızı alarm + izolasyon önerisi
 */

import type { SensorReading, GrowBag } from '../types';
import { SPECIES_INCUBATION_DAYS } from './thresholds';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ContaminationRiskResult {
  bagId: string;
  bagName: string;
  speciesId: string;
  riskScore: number;            // 0-100
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  factors: ContaminationFactor[];
  recommendation: string;
}

export interface ContaminationFactor {
  name: string;
  score: number;       // 0-100 contribution
  weight: number;      // 0-1 
  detail: string;
}

// ── Risk Score Calculation ────────────────────────────────────────────────────

/**
 * Calculate contamination risk for a single bag based on environmental data
 * and bag lifecycle metrics.
 */
export function calculateBagContaminationRisk(
  bag: GrowBag,
  reading: SensorReading | null,
  recentReadings: SensorReading[],  // last 24h
): ContaminationRiskResult {
  const factors: ContaminationFactor[] = [];

  // ── Factor 1: Night CO₂ anomaly (weight: 0.25)
  // If CO₂ is abnormally high during "quiet" hours (00:00-06:00), competing organisms may be growing
  const nightCo2Score = calculateNightCo2Score(recentReadings);
  factors.push({
    name: 'Gece CO₂ Anomalisi',
    score: nightCo2Score,
    weight: 0.25,
    detail: nightCo2Score > 50
      ? `Gece saatlerinde CO₂ normalin üstünde — rakip organizma büyüyor olabilir`
      : 'Gece CO₂ seviyeleri normal',
  });

  // ── Factor 2: TVOC spike (weight: 0.30)
  // Sudden TVOC increase indicates mold/bacteria metabolism
  const tvocScore = calculateTvocScore(reading, recentReadings);
  factors.push({
    name: 'TVOC Anomalisi',
    score: tvocScore,
    weight: 0.30,
    detail: tvocScore > 50
      ? `TVOC değerlerinde ani artış — küf/bakteri metabolizması işareti`
      : 'TVOC seviyeleri normal',
  });

  // ── Factor 3: Incubation delay (weight: 0.20)
  // If incubation is taking > 150% of typical days, weak colonization = contamination risk
  const incubationScore = calculateIncubationDelayScore(bag);
  factors.push({
    name: 'İnkubasyon Gecikmesi',
    score: incubationScore,
    weight: 0.20,
    detail: incubationScore > 50
      ? `İnkubasyon süresi normalden uzun — zayıf kolonizasyon, kontaminasyona açık`
      : 'İnkubasyon süresi normal aralıkta',
  });

  // ── Factor 4: Temperature local spike (weight: 0.15)
  // Bacterial contamination generates heat — sudden temp increase
  const tempSpikeScore = calculateTempSpikeScore(recentReadings);
  factors.push({
    name: 'Sıcaklık Spike',
    score: tempSpikeScore,
    weight: 0.15,
    detail: tempSpikeScore > 50
      ? `Anlık sıcaklık artışı — substrat içi bakteriyel ısı üretimi olabilir`
      : 'Sıcaklık trendi normal',
  });

  // ── Factor 5: Humidity pattern (weight: 0.10)
  // Rapid humidity drops can indicate bag compromise / contamination gas release
  const humidityScore = calculateHumidityPatternScore(recentReadings);
  factors.push({
    name: 'Nem Pattern Anomalisi',
    score: humidityScore,
    weight: 0.10,
    detail: humidityScore > 50
      ? `Nem değerlerinde anormal dalgalanma — substrat bozulması işareti`
      : 'Nem pattern normal',
  });

  // Weighted total
  const riskScore = Math.min(100, Math.round(
    factors.reduce((sum, f) => sum + f.score * f.weight, 0)
  ));

  const riskLevel: ContaminationRiskResult['riskLevel'] =
    riskScore >= 80 ? 'critical' :
    riskScore >= 60 ? 'high' :
    riskScore >= 35 ? 'medium' : 'low';

  const recommendation = getRecommendation(riskLevel, bag.name);

  return {
    bagId: bag.id,
    bagName: bag.name,
    speciesId: bag.speciesId,
    riskScore,
    riskLevel,
    factors,
    recommendation,
  };
}

// ── Factor Calculations ───────────────────────────────────────────────────────

function calculateNightCo2Score(readings: SensorReading[]): number {
  // Filter readings from night hours (00:00-06:00)
  const nightReadings = readings.filter(r => {
    const hour = new Date(r.timestamp).getHours();
    return hour >= 0 && hour < 6;
  });
  const dayReadings = readings.filter(r => {
    const hour = new Date(r.timestamp).getHours();
    return hour >= 8 && hour < 20;
  });

  if (nightReadings.length < 3 || dayReadings.length < 3) return 0;

  const nightAvg = nightReadings.reduce((s, r) => s + r.co2, 0) / nightReadings.length;
  const dayAvg = dayReadings.reduce((s, r) => s + r.co2, 0) / dayReadings.length;

  // If night CO₂ is higher than day (unusual — mycelium should slow at night)
  const ratio = nightAvg / Math.max(dayAvg, 1);
  if (ratio > 1.3) return Math.min(100, Math.round((ratio - 1) * 150));
  return 0;
}

function calculateTvocScore(reading: SensorReading | null, readings: SensorReading[]): number {
  if (!reading?.tvoc && readings.every(r => !r.tvoc)) return 0;

  const tvocValues = readings.filter(r => r.tvoc != null).map(r => r.tvoc!);
  if (tvocValues.length < 3) return 0;

  const avg = tvocValues.reduce((s, v) => s + v, 0) / tvocValues.length;
  const current = reading?.tvoc ?? avg;

  // A 40%+ spike above average is suspicious
  if (avg > 0 && current > avg * 1.4) {
    const spikeRatio = current / avg;
    return Math.min(100, Math.round((spikeRatio - 1) * 100));
  }

  // Absolute threshold: TVOC > 500 ppb is concerning in a grow tent
  if (current > 500) return Math.min(100, Math.round((current - 300) / 5));

  return 0;
}

function calculateIncubationDelayScore(bag: GrowBag): number {
  if (bag.status !== 'incubation') return 0;

  const speciesData = SPECIES_INCUBATION_DAYS[bag.speciesId];
  if (!speciesData) return 0;

  const daysIn = (Date.now() - new Date(bag.inoculationDate).getTime()) / 86400000;
  const typicalDays = speciesData.typicalDays;

  // If > 150% of typical days, high risk
  const ratio = daysIn / typicalDays;
  if (ratio > 1.5) return Math.min(100, Math.round((ratio - 1) * 120));
  if (ratio > 1.2) return Math.min(60, Math.round((ratio - 1) * 80));
  return 0;
}

function calculateTempSpikeScore(readings: SensorReading[]): number {
  if (readings.length < 6) return 0;

  const temps = readings.map(r => r.temperature);
  const avg = temps.reduce((s, v) => s + v, 0) / temps.length;
  const recent3 = temps.slice(-3);
  const recentAvg = recent3.reduce((s, v) => s + v, 0) / recent3.length;

  // If recent 3 readings are > 2°C above average, suspicious
  const spike = recentAvg - avg;
  if (spike > 2) return Math.min(100, Math.round(spike * 30));
  if (spike > 1) return Math.min(40, Math.round(spike * 20));
  return 0;
}

function calculateHumidityPatternScore(readings: SensorReading[]): number {
  if (readings.length < 6) return 0;

  const humidities = readings.map(r => r.humidity);
  
  // Check for rapid drops (> 8% drop between consecutive readings)
  let rapidDropCount = 0;
  for (let i = 1; i < humidities.length; i++) {
    if (humidities[i - 1] - humidities[i] > 8) rapidDropCount++;
  }

  if (rapidDropCount >= 3) return 80;
  if (rapidDropCount >= 2) return 50;
  if (rapidDropCount >= 1) return 25;
  return 0;
}

// ── Recommendations ───────────────────────────────────────────────────────────

function getRecommendation(level: ContaminationRiskResult['riskLevel'], bagName: string): string {
  switch (level) {
    case 'critical':
      return `🚨 ${bagName} — KRİTİK kontaminasyon riski! Hemen izole et, diğer torbalardan uzaklaştır. Görsel muayene yap, yeşil/siyah leke varsa derhal çıkar.`;
    case 'high':
      return `⚠️ ${bagName} — Yüksek risk. Bugün mutlaka muayene et. Şüpheli bulgu varsa diğer torbalardan ayır.`;
    case 'medium':
      return `🟡 ${bagName} — Orta risk. Önümüzdeki 24 saat içinde kontrol et. TVOC ve CO₂ trendini izle.`;
    case 'low':
      return `✅ ${bagName} — Risk düşük. Rutin kontrol yeterli.`;
  }
}

/**
 * Evaluate all active bags and return risk scores
 */
export function evaluateAllBagRisks(
  bags: GrowBag[],
  reading: SensorReading | null,
  recentReadings: SensorReading[],
): ContaminationRiskResult[] {
  const activeBags = bags.filter(b => b.status === 'incubation' || b.status === 'fruiting');
  return activeBags.map(bag => calculateBagContaminationRisk(bag, reading, recentReadings));
}

/**
 * Format a high-risk contamination alert for Telegram
 */
export function formatContaminationAlert(result: ContaminationRiskResult): string {
  const emoji = result.riskLevel === 'critical' ? '🚨' : '⚠️';
  const levelText = result.riskLevel === 'critical' ? 'KRİTİK' : 'YÜKSEK';
  
  const topFactors = result.factors
    .filter(f => f.score > 30)
    .sort((a, b) => b.score * b.weight - a.score * a.weight)
    .slice(0, 3);

  const lines = [
    `${emoji} <b>KONTAMİNASYON ${levelText} — ${result.bagName}</b>`,
    '',
    `🎯 Risk Skoru: <b>${result.riskScore}/100</b>`,
    '',
    '📊 Faktörler:',
    ...topFactors.map(f => `• ${f.name}: ${f.detail}`),
    '',
    `💡 ${result.recommendation}`,
  ];

  return lines.join('\n');
}
